/**
 * ADR 189 · §8 — El carrito se agrupa por ENTREGA, no por origen.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * EL CARRITO ES UN RECIBO, NO UN FORMULARIO
 *
 * Este archivo perdió peso tres veces seguidas, y las tres por la misma razón:
 * seguía pidiendo decisiones que no le tocaban.
 *
 *   · F6 puso dos tarjetas tipo radio para comparar recoger contra envío, más
 *     un selector de punto que guardaba su elección como atributo del carrito.
 *   · F8 las comprimió a dos renglones, y después a dos pestañas de simulación,
 *     porque el radio PROMETE que elegir ahí cuenta — y no contaba: el modo se
 *     elige en el checkout, y el comprador sentía que decidía dos veces.
 *   · Y al final se quitó la pregunta entera. **El checkout es el único dueño
 *     de la entrega, modo Y punto**, vía tarifas estáticas por punto
 *     (`🏛️ Recoge en {punto}`, autogeneradas). Aquí no hay nada que elegir.
 *
 * UNA DECISIÓN, UN DUEÑO. Lo que queda es lo que sólo el carrito puede decir, y
 * que ninguna tarifa estática alcanza a contar a tiempo:
 *
 *   - En cuántas entregas llega el pedido y en qué fechas.
 *   - Qué carta llega cuándo, y de dónde viene la que tarda.
 *   - Que la segunda entrega es OTRA IDA AL MISMO PUNTO — la línea de una
 *     tarifa del checkout no tiene dónde decir eso, y descubrirlo después es
 *     volver a casa con la mitad del pedido.
 *   - Qué pasa si al pagar elige envío: un paquete cuando esté completo, y su
 *     precio real contra el umbral.
 *
 * El plan se calcula para el escenario RECOGER, que es ~88% de los pedidos, y
 * por eso no hace falta preguntarlo. Cuando se sabe que recoger NO existe para
 * esa región —cero puntos— no se agrupa: ahí todo sale en un paquete y prometer
 * dos llegadas sería mentir.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * LA FORMA DEL RECIBO
 *
 *   CADA ENTREGA ES UNA CAJA CON SU CABECERA, Y LA CAJA SE PLIEGA. Nacen
 *   ABIERTAS: lo primero que hace cualquiera al abrir su carrito es mirar qué
 *   trae. Plegar es la herramienta para cuando una entrega larga estorba. La
 *   cabecera trae la fecha, cuántas cartas, cuánto suman y un stack de
 *   miniaturas, así que plegada sigue siendo revisable.
 *
 *   LA CAJA SE DIBUJA CON CSS SOBRE LAS FILAS QUE YA ESTÁN. Nada se reparenta:
 *   los bordes laterales van en la primera y la última celda de cada `<tr>`, el
 *   fondo en todas, y la última fila del grupo —marcada desde el JS, porque
 *   `:last-child` no sabe dónde termina un grupo— cierra con borde y esquinas.
 *
 *   PLEGAR ES ESCONDER, NUNCA DESMONTAR. Las filas se quedan en su `<tbody>`
 *   con su `data-index` y su input: `cart.js` de Dawn las sigue encontrando, y
 *   editar dentro de un grupo funciona igual que siempre. Y lo que el comprador
 *   plegó sigue plegado después de que Dawn reemplace el HTML — el estado vive
 *   en el módulo, con el id del grupo por clave, que no cambia entre renders.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DEGRADACIÓN (todo detrás de checks, como hizo F5 con la valija)
 *
 * Este módulo se estrena cuando el backfill de regiones todavía NO corrió, así
 * que el estado degradado no es el caso raro: es el día 1. Por eso degrada por
 * pieza y no todo junto.
 *
 *   - Sin dato de región en alguna línea → NO agrupa y NO promete fechas. Se
 *     queda la lista plana de siempre, con el pie y la línea de envío, que sí
 *     son ciertos sin ese dato.
 *   - Sin calendario de valija (F3 sin configurar) y con cartas foráneas → no
 *     se puede fechar la segunda entrega, así que no se agrupa. Prometer una
 *     corrida que no está calendarizada es el error del ⚡ otra vez.
 *   - Sin `/api/regiones/estado` → no se sabe el umbral de envío gratis, y la
 *     regla dura manda: nunca se afirma GRATIS.
 *   - Región conocida y sin un solo punto → no se agrupa, y se dice por qué.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * QUÉ NO HACE
 *
 *   - No escribe NADA en el carrito. El atributo `Punto de entrega` que F6
 *     guardaba murió con el selector: el punto es una tarifa del checkout.
 *   - No adivina la región del comprador por IP (ADR 189 §6). Se lee del mismo
 *     `localStorage` que escribe el selector del header, y ya.
 *   - No modela la tarifa `📦 Envío voluminoso` de ADR 190 ($250 desde 1 kg).
 *     No hace falta: esa tarifa se SUMA a la lista del checkout en vez de
 *     reemplazar a `🚚 Envío a domicilio`, que no tiene condición de peso — así
 *     que la opción de envío más barata siempre es la que este carrito nombra.
 *   - No promete pedidos mixtos (unas cartas recogidas y otras enviadas). Eso
 *     son dos pedidos y el ADR lo da por perdido a propósito.
 */
(function () {
  /* ───────────────────── El catálogo, copiado del contrato ─────────────────
     Estos cuatro valores son de ADR 190 (`lib/services/shopify/zonas.ts`) y no
     de este archivo. Si allá cambian, aquí se cambian: el carrito diciendo un
     precio y el checkout cobrando otro es exactamente lo que este ADR quita.

     `🚚 Envío a domicilio` va con su emoji porque es el literal de la pantalla
     de pago: nombrar la tarifa igual es lo que hace que quien lea la línea de
     envío la reconozca al llegar allá. Las de recoger ya no se nombran aquí —
     hay una por punto y las genera el backend, así que el carrito no puede
     saber cuál verá. */
  var TARIFA_ENVIO = '🚚 Envío a domicilio';
  var PRECIO_ENVIO = 200;
  var UMBRAL_CON_CENTRAL = 2500;
  var UMBRAL_SIN_CENTRAL = 5000;

  /* El SLA de ADR 189, el mismo que publica la ficha. Nada fuera de aquí se
     promete. Como en el carrito hay que decir CUÁNDO y no "en cuántos días",
     el rango se convierte en fechas — pero sigue siendo un rango: publicar un
     día exacto donde el SLA da tres sería inventar precisión. */
  var SLA_MIN = 7;
  var SLA_MAX = 10;
  /* Días de tránsito de la valija cuando el endpoint no los trae. */
  var TRANSITO_POR_OMISION = 3;

  var ESTADO_URL = 'https://cards.the-vault.mx/api/regiones/estado';

  var LLAVE_REGION = 'vault_region';


  /* ───────────────────────── localStorage ─────────────────────────
     Siempre en try/catch: en modo privado el acceso TIRA al construirse, no al
     escribir. Patrón del repo (vault-region.js). */
  function leerLocal(llave) {
    try {
      return localStorage.getItem(llave) || '';
    } catch (e) {
      return '';
    }
  }

  function medir(evento, destino) {
    if (typeof window.vaultEvento !== 'function') return;
    window.vaultEvento(evento, destino === undefined || destino === null ? null : String(destino));
  }

  /* ───────────────────────── Fechas ─────────────────────────
     Misma gramática que `fechaLarga` de vault-entrega.js (F5): la fecha se
     construye a MEDIODÍA UTC porque leer 'YYYY-MM-DD' como medianoche UTC la
     deja un día atrás al formatearla en horario de México.

     PENDIENTE DE MERGE: F5 tiene su propia copia privada dentro de su IIFE.
     Cuando las dos ramas estén en master conviene extraer estas tres funciones
     a un `assets/vault-fechas.js` y que las dos lo usen; hoy no se puede sin
     tocar F5, y F6 no depende de F5 a propósito. */
  function aFecha(iso, masDias) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return null;
    var d = new Date(iso + 'T12:00:00Z');
    if (isNaN(d.getTime())) return null;
    if (masDias) d.setUTCDate(d.getUTCDate() + masDias);
    return d;
  }

  function partes(d) {
    try {
      var fmt = new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });
      var salida = { dia: '', mes: '', semana: '' };
      var lista = fmt.formatToParts(d);
      for (var i = 0; i < lista.length; i++) {
        if (lista[i].type === 'day') salida.dia = lista[i].value;
        if (lista[i].type === 'month') salida.mes = lista[i].value;
        if (lista[i].type === 'weekday') salida.semana = lista[i].value;
      }
      return salida.dia && salida.mes ? salida : null;
    } catch (e) {
      return null;
    }
  }

  /** "jueves 11 de septiembre" */
  function fechaLarga(iso, masDias) {
    var d = aFecha(iso, masDias);
    var p = d && partes(d);
    if (!p) return '';
    return p.semana + ' ' + p.dia + ' de ' + p.mes;
  }

  /* ── F8: las mismas fechas, en corto ──────────────────────────────────────
     El comparador comprimido y la barra fija tienen un renglón, no un párrafo:
     "alrededor del jueves 11 de septiembre" no cabe y truncado no se lee. La
     versión corta dice LO MISMO con menos —incluida la incertidumbre, que es lo
     primero que se pierde al abreviar mal: el `~` de la fecha aproximada se
     queda, porque sin él el carrito prometería un día exacto donde el SLA da
     tres. El detalle largo sigue completo en la cabecera de cada grupo. */
  function partesCortas(d) {
    try {
      var fmt = new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City',
        day: 'numeric',
        month: 'short',
      });
      var salida = { dia: '', mes: '' };
      var lista = fmt.formatToParts(d);
      for (var i = 0; i < lista.length; i++) {
        if (lista[i].type === 'day') salida.dia = lista[i].value;
        /* es-MX abrevia con punto ("sept."); el punto sobra en una línea que ya
           está llena de separadores. */
        if (lista[i].type === 'month') salida.mes = lista[i].value.replace(/\.$/, '');
      }
      return salida.dia && salida.mes ? salida : null;
    } catch (e) {
      return null;
    }
  }

  /** "11 sep" */
  function fechaCorta(iso) {
    var p = partesCortas(aFecha(iso));
    return p ? p.dia + ' ' + p.mes : '';
  }

  /** "~11 sep" para la fecha aproximada, "2–5 sep" para el rango. */
  function fechaCortaDe(f) {
    if (!f) return '';
    if (f.tipo !== 'rango') {
      var suelta = fechaCorta(f.d);
      return suelta ? '~' + suelta : '';
    }
    var a = partesCortas(aFecha(f.a));
    var b = partesCortas(aFecha(f.b));
    if (!a || !b) return '';
    if (a.mes === b.mes) return a.dia + '–' + b.dia + ' ' + b.mes;
    return a.dia + ' ' + a.mes + '–' + b.dia + ' ' + b.mes;
  }

  /** "del 2 al 5 de septiembre" — y con el mes de los dos lados si cruza mes. */
  function fechaRango(isoA, isoB) {
    var a = partes(aFecha(isoA));
    var b = partes(aFecha(isoB));
    if (!a || !b) return '';
    if (a.mes === b.mes) return 'del ' + a.dia + ' al ' + b.dia + ' de ' + b.mes;
    return 'del ' + a.dia + ' de ' + a.mes + ' al ' + b.dia + ' de ' + b.mes;
  }

  function sumarDias(iso, dias) {
    var d = aFecha(iso, dias);
    return d ? d.toISOString().slice(0, 10) : '';
  }

  /** Hoy en horario de México, no en el del navegador. */
  function hoyMx() {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(
        new Date()
      );
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  }

  function pesos(n) {
    try {
      return '$' + Number(n).toLocaleString('es-MX');
    } catch (e) {
      return '$' + n;
    }
  }

  /* ───────────────────────── El núcleo, puro y probable ───────────────────── */

  function nodosDe(regiones, estado) {
    var info = regiones && estado ? regiones[estado] : null;
    return info && info.nodos ? info.nodos : 0;
  }

  function tieneCentral(regiones, estado) {
    var info = regiones && estado ? regiones[estado] : null;
    /* "Central activa" es lo que ADR 190 llama `centralActiva`, y una región
       llega ahí por tener hub: es el mismo dato con el que se le crea su zona
       con umbral de $2,500. Sin `info` no se sabe, y no saber no es "no". */
    return info ? Boolean(info.hub) : null;
  }

  /**
   * El GRUPO de una región: el nombre canónico del hub que la sirve.
   *
   * CDMX y Estado de México comparten central, así que una carta de Edomex para
   * alguien de CDMX no viaja en ninguna valija — es acopio local del mismo hub y
   * llega en el plazo normal. Antes de esto el carrito la mandaba a "segunda
   * entrega · desde Estado de México", que es prometer un viaje que no ocurre.
   * Es el mismo error que ya se corrigió en el selector de puntos (los puntos
   * son del hub, no de la región), ahora en el agrupado.
   *
   * El campo lo trae `/api/regiones/estado` por región. Cuando no está —una
   * caché de ayer, o el endpoint caído— cada región es su propio grupo, y la
   * regla se reduce exactamente a la de antes: comparar nombres de región. Por
   * eso el fallback no necesita una rama aparte.
   */
  function grupoDe(regiones, region) {
    var info = regiones && region ? regiones[region] : null;
    return (info && info.grupo) || region || '';
  }

  /**
   * El precio del envío, calculado del total del carrito contra el umbral de la
   * región DEL COMPRADOR.
   *
   * REGLA DURA DE HONESTIDAD: nunca se afirma GRATIS si su región no se conoce.
   * El umbral es $2,500 donde hay central y $5,000 donde no, así que decir
   * "gratis" sin saber cuál de los dos aplica es mentirle a quien más compara,
   * justo al revés de lo que este ADR viene a hacer.
   *
   * `total` viene en CENTAVOS (es lo que da `cart.total_price` de Liquid).
   */
  function precioDeEnvio(total, regionComprador, regiones) {
    var central = regionComprador ? tieneCentral(regiones, regionComprador) : null;

    if (central === null) {
      return {
        gratis: false,
        precio: PRECIO_ENVIO,
        etiqueta: pesos(PRECIO_ENVIO),
        nota: 'gratis desde ' + pesos(UMBRAL_CON_CENTRAL) + ' en regiones con central',
      };
    }

    var umbral = central ? UMBRAL_CON_CENTRAL : UMBRAL_SIN_CENTRAL;
    if (Number(total) / 100 >= umbral) {
      return { gratis: true, precio: 0, etiqueta: 'GRATIS', nota: '' };
    }
    return {
      gratis: false,
      precio: PRECIO_ENVIO,
      etiqueta: pesos(PRECIO_ENVIO),
      nota: 'gratis desde ' + pesos(umbral),
    };
  }

  /**
   * La fecha en que llega lo que espera la valija, o null si no se puede fechar.
   *
   * Las dos cadencias no comparten copy a propósito (ADR 189): con corrida
   * quincenal un plazo plano miente —quien compra al día siguiente esperaría
   * casi 20 días—, así que se publica LA FECHA de la próxima corrida; con
   * corrida semanal el error máximo baja a ±3 días y son 7 días planos.
   */
  function fechaDeValija(valija, hoy) {
    if (!valija) return null;
    if (valija.cadencia === 'semanal') return sumarDias(hoy, 7) || null;
    if (valija.cadencia !== 'quincenal') return null;
    var transito = valija.transito == null ? TRANSITO_POR_OMISION : valija.transito;
    var fecha = sumarDias(valija.proxima, transito);
    return fecha || null;
  }

  /** La región de referencia: la del comprador si se sabe; si no, la que despacha. */
  function regionDeReferencia(lineas, regionComprador, regiones) {
    if (regionComprador) return regionComprador;

    /* No se sabe su región. Se toma la que más manda en el carrito, prefiriendo
       una que tenga dónde recoger: es el mismo criterio de `regionQueDespacha`
       de F5, y el copy que sale de ahí es cierto para todos — con 95.8% del
       catálogo en CDMX, la carta se recoge donde está. */
    var cuenta = {};
    var orden = [];
    for (var i = 0; i < lineas.length; i++) {
      var rs = lineas[i].regiones || [];
      for (var j = 0; j < rs.length; j++) {
        if (cuenta[rs[j]] === undefined) {
          cuenta[rs[j]] = 0;
          orden.push(rs[j]);
        }
        cuenta[rs[j]] += 1;
      }
    }
    var mejor = '';
    var mejorPuntaje = -1;
    for (var k = 0; k < orden.length; k++) {
      var r = orden[k];
      /* Con nodos pesa más: es la única que puede ofrecer recoger. */
      var puntaje = cuenta[r] + (nodosDe(regiones, r) > 0 ? 1000 : 0);
      if (puntaje > mejorPuntaje) {
        mejorPuntaje = puntaje;
        mejor = r;
      }
    }
    return mejor;
  }

  function cartas(n) {
    return n === 1 ? '1 carta' : n + ' cartas';
  }

  /**
   * Cuántas cartas y cuánto suma un grupo (F8).
   *
   * `subtotal` en centavos, o `null` si alguna línea no trae precio. Publicar
   * una suma a la que le falta un renglón es peor que no publicarla: el
   * comprador la usaría para cuadrar el total y no cuadraría.
   */
  function resumirGrupo(grupo) {
    var n = 0;
    var suma = 0;
    var completo = true;
    for (var i = 0; i < grupo.lineas.length; i++) {
      n += grupo.lineas[i].cantidad || 1;
      var p = grupo.lineas[i].precio;
      if (p === null || p === undefined || isNaN(p)) completo = false;
      else suma += p;
    }
    grupo.cartas = n;
    grupo.subtotal = completo ? suma : null;
    return grupo;
  }

  function textoDeFecha(f) {
    if (!f) return '';
    if (f.tipo === 'rango') return fechaRango(f.a, f.b);
    return 'alrededor del ' + fechaLarga(f.d);
  }

  function ordenDeFecha(f) {
    return f.tipo === 'rango' ? f.a : f.d;
  }

  /**
   * El plan de entrega del carrito. Función pura: es lo único que vale la pena
   * probar de este archivo, y el tema no tiene build donde correr pruebas de
   * otra forma — así que se expone abajo para el harness de `node:vm`.
   *
   * ctx = {
   *   lineas: [{ clave, regiones: [], single: bool, express: bool, cantidad: n }],
   *   regionComprador: 'CDMX' | '',
   *   regiones: { CDMX: {hub, nodos, catalogo, grupo} } | null,
   *   valija: { cadencia, proxima, transito } | null,
   *   puntos: { CDMX: [{nombre, colonia, horarios}] } | null,
   *   puntosPreferidos: ['Rockshow', ...],  // en orden de preferencia
   *   total: 250000,   // centavos
   *   hoy: '2026-08-26'
   * }
   */
  function planDeEntrega(ctx) {
    var lineas = (ctx && ctx.lineas) || [];
    var regiones = (ctx && ctx.regiones) || null;
    var rc = (ctx && ctx.regionComprador) || '';
    var hoy = (ctx && ctx.hoy) || hoyMx();

    var regionBase = regionDeReferencia(lineas, rc, regiones);

    /* ¿Puede este pedido recogerse?
       El plan se calcula para el escenario RECOGER, que es el 88% real de los
       pedidos, y por eso no hay que preguntarlo. Pero cuando se SABE que no
       existe —región del comprador conocida y su zona sin un solo punto— no se
       agrupa: ahí todo sale por paquetería, en un solo envío, y publicar
       "primera entrega / segunda entrega · otra ida al mismo punto" sería
       prometer dos llegadas y un punto que no hay (ADR 189 §5). */
    var recogerDisponible = true;
    if (rc && regiones && Object.keys(regiones).length && nodosDe(regiones, rc) === 0) {
      recogerDisponible = false;
    }

    var envio = precioDeEnvio(ctx ? ctx.total : 0, rc, regiones);

    var plan = {
      regionBase: regionBase,
      regionConocida: Boolean(rc),
      recogerDisponible: recogerDisponible,
      total: (ctx && ctx.total) || 0,
      envio: envio,
      agrupa: false,
      /* Los grupos por LLEGADA del escenario recoger. Antes esto era
         `{recoger, envio}` porque el carrito enseñaba los dos futuros; ahora el
         modo lo decide el checkout con sus tarifas y aquí sólo se cuenta uno. */
      grupos: [],
      entregas: 0,
      resumen: { recoger: '', primeraCorta: '' },
    };

    if (!recogerDisponible) return plan;

    if (!lineas.length) return plan;

    /* ── Repartir las líneas ────────────────────────────────────────────────
       Tres cortes antes de mirar regiones, y los tres son "esta línea no espera
       ninguna valija":

         1. ⚡EXPRESS. Una carta express YA está físicamente en un punto — eso es
            lo que la hace express. Mandarla a esperar la corrida de la semana
            porque su metafield de región dice otro estado es la contradicción
            más cara que puede tener esta pantalla: la ficha promete horas y el
            carrito promete quincenas. Va al grupo local digan lo que digan sus
            regiones, y tampoco cuenta como dato faltante: de una express sí se
            sabe dónde está. (La promesa fina de Express se queda en la ficha;
            aquí sólo se garantiza que nunca espere valija.)
         2. Un SELLADO no lleva promesa regional: se asigna por variante directa
            y nunca pasa por el desempate regional del webhook (ADR 189 §3). Cae
            al grupo local, sin copy de región.
         3. Sin dato de región no se puede decidir, y eso bloquea el agrupado
            entero más abajo. */
    var locales = [];
    var foraneas = [];
    var faltaDato = false;
    var grupoBase = grupoDe(regiones, regionBase);

    for (var i = 0; i < lineas.length; i++) {
      var l = lineas[i];
      var rs = l.regiones || [];

      if (l.express) {
        locales.push(l);
        continue;
      }
      if (!l.single) {
        locales.push(l);
        continue;
      }
      if (!rs.length) {
        faltaDato = true;
        locales.push(l);
        continue;
      }
      if (!regionBase) {
        faltaDato = true;
        locales.push(l);
        continue;
      }
      /* Local = la sirve el MISMO HUB, no = es la misma región. Sin el campo
         `grupo`, `grupoDe` devuelve el nombre de la región y esto vuelve a ser
         la comparación de antes. */
      var mismoHub = false;
      for (var h = 0; h < rs.length; h++) {
        if (grupoDe(regiones, rs[h]) === grupoBase) {
          mismoHub = true;
          break;
        }
      }
      if (mismoHub) {
        locales.push(l);
        continue;
      }
      foraneas.push(l);
    }

    var fechaValija = fechaDeValija(ctx ? ctx.valija : null, hoy);

    /* ── ¿Se puede agrupar y prometer? ──────────────────────────────────────
       Tres condiciones, y las tres protegen la misma cosa: no fechar lo que no
       se sabe fechar.
         1. Ninguna línea sin dato de región — con el backfill a medias,
            agrupar sería decir "llega el 28" de una carta que quizá esté en
            Aguascalientes. Sobre-prometer es el error caro.
         2. Una región de referencia, o no hay contra qué comparar.
         3. Si hay cartas foráneas, un calendario de valija que las feche. */
    if (faltaDato || !regionBase) return plan;
    if (foraneas.length && !fechaValija) return plan;

    plan.agrupa = true;

    var fechaLocal = { tipo: 'rango', a: sumarDias(hoy, SLA_MIN), b: sumarDias(hoy, SLA_MAX) };

    /* ── Grupos con RECOGER: uno por LLEGADA, no por modo ───────────────────
       Dos idas al mismo punto en fechas distintas son dos grupos, porque son
       dos viajes del comprador. Fundirlas sería la mentira más cara: la persona
       iría una vez y volvería con la mitad. */
    var gruposRecoger = [];
    if (locales.length) {
      /* `marcar` es qué renglones llevan "desde {región}". En el grupo local va
         vacío a propósito: ahí da igual de dónde venga la carta, y escribirlo
         sería ruido en el 95.8% del catálogo (ADR 189 §8). */
      gruposRecoger.push({ id: 'local', fecha: fechaLocal, lineas: locales, marcar: [] });
    }
    if (foraneas.length) {
      gruposRecoger.push({
        id: 'valija',
        fecha: { tipo: 'punto', d: fechaValija },
        lineas: foraneas,
        marcar: foraneas,
      });
    }
    gruposRecoger.sort(function (a, b) {
      return ordenDeFecha(a.fecha) < ordenDeFecha(b.fecha) ? -1 : 1;
    });

    for (var g = 0; g < gruposRecoger.length; g++) {
      var grupo = gruposRecoger[g];
      /* El título nombra la ENTREGA, no el punto (F8). F6 los llamaba "Recoges
         en Hobby Mart" y "Recoges en Hobby Mart, segunda vuelta", que en un
         teléfono ocupaba tres líneas por cabecera — y repetía el nombre del
         punto que el comparador ya dice arriba y la barra fija repite al bajar.
         Con dos idas al mismo sitio, lo que las distingue es cuál es primera y
         cuál segunda, que es exactamente lo que ahora dicen.

         Y desde que el punto se elige al pagar (como tarifa), el carrito ni
         siquiera SABE cuál será: nombrarlo aquí habría sido adivinarlo. Lo que
         no se toca es la honestidad — el aviso del ADR sigue con todas sus
         letras. */
      grupo.titulo =
        gruposRecoger.length === 1 ? 'Tu entrega' : g === 0 ? 'Primera entrega' : 'Segunda entrega';
      grupo.detalle = textoDeFecha(grupo.fecha);
      grupo.detalleCorto = fechaCortaDe(grupo.fecha);
      /* "Sí, es otra ida al mismo punto" — con todas sus letras, que es como el
         ADR pide que se diga. */
      grupo.aviso = g === 0 ? '' : 'Sí, es otra ida al mismo punto.';
      grupo.icono = 'tienda';
    }
    plan.grupos = gruposRecoger;
    plan.entregas = gruposRecoger.length;

    /* ── Lo que cada grupo dice de sí mismo (F8) ────────────────────────────
       Un grupo se puede plegar, así que la cabecera tiene que bastar para
       revisar sin abrirlo: cuántas cartas y cuánto suman. El subtotal es `null`
       —y no cero— cuando alguna línea no trae precio: media suma es peor que
       ninguna. */
    for (var g2 = 0; g2 < gruposRecoger.length; g2++) resumirGrupo(gruposRecoger[g2]);

    var primera = gruposRecoger[0];
    plan.resumen.recoger =
      gruposRecoger.length > 1
        ? 'Llega en ' + gruposRecoger.length + ' entregas — la primera ' + textoDeFecha(primera.fecha) + '.'
        : 'Llega ' + textoDeFecha(primera.fecha) + '.';
    plan.resumen.primeraCorta = fechaCortaDe(primera.fecha);

    return plan;
  }

  /* ───────────────────────── Iconos ─────────────────────────
     Gramática de F5: SVG de trazo sobre lienzo de 24, `currentColor`, dibujado
     a 16px. El icono ES el modo de entrega — tienda = recoges, caja =
     paquetería, pin = el lugar. Nada de emoji: el emoji cambia de forma con el
     sistema operativo y no hereda el color del texto. */
  var ICONOS = {
    tienda:
      '<path d="M3 9.5 4.4 4h15.2L21 9.5"/><path d="M3 9.5h18v1.2a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z"/><path d="M4.6 13.4V20h14.8v-6.6"/>',
    caja: '<path d="M12 3 3.5 7.2v9.6L12 21l8.5-4.2V7.2Z"/><path d="M3.5 7.2 12 11.4l8.5-4.2"/><path d="M12 11.4V21"/>',
    pin: '<path d="M12 21s6.5-5.6 6.5-11a6.5 6.5 0 1 0-13 0c0 5.4 6.5 11 6.5 11Z"/><circle cx="12" cy="10" r="2.4"/>',
    /* F8: el chevron del acordeón. Apunta a la derecha y lo gira el CSS al
       expandir — una sola forma que rota es más barata que dos y no parpadea. */
    chevron: '<path d="m9 5 7 7-7 7"/>',
  };

  function icono(nombre) {
    var span = document.createElement('span');
    span.className = 'vault-carrito__icono';
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">' +
      (ICONOS[nombre] || '') +
      '</svg>';
    return span;
  }

  function elemento(etiqueta, clase, texto) {
    var el = document.createElement(etiqueta);
    if (clase) el.className = clase;
    if (texto) el.textContent = texto;
    return el;
  }

  /* ───────────────────────── Estado del módulo ─────────────────────────
     El MODO vive aquí y sólo aquí: no se guarda en el carrito (Shopify no deja
     preseleccionar tarifa) ni en localStorage (no es una preferencia, es la
     comparación de este momento). Sobrevive a los re-render de Dawn porque el
     módulo no se recarga, y se pierde al recargar la página. Está bien. */
  var entregasAlPagar = 0;
  /* La barra de pago (F9). Vive colgada de <body> para que el re-render de Dawn
     no se la lleve, y se crea una sola vez. */
  var barraPago = null;
  var barraAgendada = false;
  var totalPintado = '';
  var datos = { regiones: null, valija: null, listo: false };
  /* El panel que se pintó la última vez. Dawn reemplaza el HTML de
     `.js-contents` en cada cambio de cantidad, así que "hay un panel distinto
     al que pinté" es la señal exacta de que hay que repintar — y no se confunde
     con las mutaciones que hace este mismo archivo, que es la trampa que F5
     documenta en la ficha. */
  var pintados = [];

  /* ── F8: qué grupos plegó el comprador ────────────────────────────────────
     Los grupos NACEN ABIERTOS. Se probó al revés —el carrito entero como un
     recibo de dos renglones— y esconder las cartas por omisión resultó ser un
     paso de más: lo primero que hace cualquiera al abrir su carrito es mirar
     qué trae. Plegar es la herramienta para cuando estorban, no el estado de
     partida. Lo que la agrupación aporta se ve igual con todo abierto, porque
     cada grupo se dibuja como una caja con su cabecera.

     Vive en memoria de página y NO en localStorage a propósito: no es una
     preferencia del comprador, es dónde está parado en ESTA revisión. Lo que sí
     tiene que sobrevivir es el re-render de Dawn —plegar un grupo, cambiar una
     cantidad en otro y que el primero se abriera solo sería deshacerle el
     trabajo—, y sobrevive porque el módulo no se recarga: la clave es el id del
     grupo ('local' / 'valija' / 'envio'), que es estable entre renders porque lo
     decide el plan y no el DOM.

     Ausente = abierto. Así un grupo nuevo (aparece la primera carta foránea del
     carrito) nace abierto como todos, sin una entrada que ponerle antes. */
  var plegados = {};


  /* ───────────────────────── Los datos remotos ───────────────────────── */

  /**
   * La caché del día que escribe `vault-region.js`, leída DIRECTO.
   *
   * Sin esto el carrito espera a la red para pintar sus grupos, y hasta que
   * conteste se ve la lista plana: medido en Chromium, 533 ms con la latencia
   * real del endpoint y 2,031 ms en una red tipo 3G. Lo caro no era el fetch,
   * era depender de que `window.vaultRegion` ya estuviera definido: el layout lo
   * carga sin `defer` y este módulo con `defer`, pero bastaba con que el orden
   * cambiara —o con que alguien recortara el layout— para volver a la red
   * teniendo el dato en el bolsillo.
   *
   * La llave es la MISMA que allá, fecha incluida y en UTC como la calcula
   * `vault-region.js`: si aquí se "arreglara" a horario de México, los dos
   * escribirían y leerían llaves distintas cerca de la medianoche y esto no
   * acertaría nunca. Copiada, no mejorada.
   *
   * DECISIÓN, con su residual: queda un flash de lista plana en la PRIMERA
   * visita del día en frío (~533 ms). Se acepta. Cerrarlo del todo pediría
   * pintar los grupos desde Liquid —que no sabe la región del comprador, vive
   * en su navegador— o bloquear el render, que es peor que un parpadeo. Como
   * `vault-region.js` corre en cualquier página del sitio, casi nadie llega al
   * carrito con la caché fría.
   */
  var LLAVE_ESTADO = 'vault_regiones_v3_' + new Date().toISOString().slice(0, 10);

  function estadoCacheado() {
    try {
      var raw = localStorage.getItem(LLAVE_ESTADO);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (d && d.regiones) return d;
      /* Una caché vieja puede tener el mapa pelado en la raíz, sin `valija`. */
      if (d && typeof d === 'object' && Object.keys(d).length) {
        return { regiones: d, valija: null };
      }
      return null;
    } catch (e) {
      /* En modo privado el acceso TIRA al construirse, no al leer. */
      return null;
    }
  }

  function pedirEstado(cb) {
    /* Primero el bolsillo: si el dato del día ya está guardado, esto contesta
       SÍNCRONO y los grupos entran en el primer pintado, sin parpadeo. */
    var enCache = estadoCacheado();
    if (enCache) return cb(enCache);

    /* Si F5 ya está en el tema, su caché de un día sirve a los dos: una sola
       petición para toda la visita. Es DETECCIÓN DE PRESENCIA, no dependencia —
       F6 no se apoya en que F5 esté mergeado, y sin él pide el endpoint solo. */
    if (window.vaultRegion && typeof window.vaultRegion.datos === 'function') {
      window.vaultRegion.datos(function (payload) {
        if (payload && payload.regiones) cb(payload);
        /* Una caché vieja del navegador puede tener el mapa pelado en la raíz. */
        else cb(payload ? { regiones: payload, valija: null } : null);
      });
      return;
    }
    fetch(ESTADO_URL, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (d) {
        cb(d && d.regiones ? d : null);
      })
      .catch(function () {
        cb(null);
      });
  }

  /* ───────────────────────── Leer el carrito del DOM ───────────────────── */

  /** Acepta "CDMX|Jalisco" y "CDMX,Jalisco": el metafield puede llegar de las dos formas. */
  function listaDe(valor) {
    return (valor || '')
      .split(/[|,]/)
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  /**
   * La miniatura de la línea, reusando la imagen que Dawn YA pintó en la fila.
   *
   * A propósito no se emite una URL nueva desde Liquid: el stack la dibuja a
   * 26px y la fila a 100px, pero si las dos apuntan al MISMO `src` el navegador
   * no pide nada extra. Emitir `image_url: width: 96` habría costado una
   * petición por carta sólo para ahorrarse unos bytes de decodificado.
   */
  function imagenDe(fila) {
    var img = fila.querySelector('.cart-item__image');
    return img && img.getAttribute('src') ? img.getAttribute('src') : '';
  }

  function lineasDe(raiz) {
    var filas = raiz.querySelectorAll('[data-vault-linea]');
    var lineas = [];
    for (var i = 0; i < filas.length; i++) {
      /* `data-precio` es el total de la línea en centavos (`final_line_price`).
         Si falta —una plantilla vieja en caché, un tema a medio desplegar— se
         guarda `null` y el subtotal del grupo NO se publica: un recibo con una
         suma incompleta miente peor que un recibo sin suma. */
      var crudo = filas[i].getAttribute('data-precio');
      lineas.push({
        clave: filas[i].id || 'linea-' + i,
        fila: filas[i],
        regiones: listaDe(filas[i].getAttribute('data-regiones')),
        single: filas[i].getAttribute('data-single') === '1',
        /* ⚡Express: la variante ya está en un punto, así que esta línea nunca
           espera valija. Lo emite el snippet desde el metafield de VARIANTE. */
        express: filas[i].getAttribute('data-express') === '1',
        cantidad: parseInt(filas[i].getAttribute('data-cantidad'), 10) || 1,
        precio: crudo === null || crudo === '' ? null : parseInt(crudo, 10),
        imagen: imagenDe(filas[i]),
      });
    }
    return lineas;
  }

  /* ───────────────────────── El pie ─────────────────────────
     Nombra las tarifas EXACTAMENTE como el checkout las va a nombrar, emoji
     incluido: son literales de la pantalla de pago, no adornos. Ése sigue siendo
     todo su trabajo.

     Lo que dejó de hacer es predecir preselecciones. F6 escribió aquí tres
     variantes —"aparece X ya seleccionada", "cámbiala a Y"— porque el comparador
     de arriba prometía una decisión que no tomaba, y el pie tenía que ir
     deshaciendo el enredo con más letras cada vez. Con las pestañas de
     simulación no hay enredo que deshacer: arriba se ve qué significa cada
     forma, aquí se dice dónde se elige, y ya.

     La regla dura sigue en pie por construcción: este texto no afirma ningún
     precio, así que no hay forma de que diga GRATIS sin conocer la región. */
  function textoDelPie(plan) {
    if (!plan.recogerDisponible) {
      /* Su región no tiene punto: ahí no hay nada que elegir, y decir "eliges"
         sería mandarlo a buscar una opción que no está en la lista. */
      return 'En ' + (plan.regionBase || 'tu región') + ' todavía no hay punto, así que tu pedido va por envío.';
    }
    return 'La forma de entrega —recoger en cuál punto, o envío— la eliges al pagar.';
  }

  /**
   * La línea de envío, que es lo único que este carrito dice del OTRO futuro.
   *
   * Va cerca del total y no arriba, porque no es una opción que se toque: es el
   * dato que le falta a alguien que está a $300 de cruzar el umbral. Y es lo
   * único que una tarifa estática no puede contarle a tiempo — cuando la vea en
   * el checkout ya decidió.
   *
   * La regla dura vive en `precioDeEnvio` y por eso no se repite aquí: sin
   * región conocida la etiqueta nunca es GRATIS, y el umbral que se nombra va
   * condicionado ("en regiones con central").
   */
  function textoDeEnvio(plan) {
    return (
      'Si al pagar eliges ' +
      TARIFA_ENVIO +
      ': sale en un paquete cuando el pedido esté completo — ' +
      plan.envio.etiqueta +
      (plan.envio.nota ? ', ' + plan.envio.nota : '') +
      '.'
    );
  }

  function pintarPie(plan) {
    var pies = document.querySelectorAll('.cart__delivery-estimate');
    for (var i = 0; i < pies.length; i++) {
      pies[i].textContent = textoDelPie(plan);
      pies[i].setAttribute('data-vault-pie', '1');
      /* La línea de envío se cuelga JUNTO al pie —o sea, junto al total— y no
         dentro del panel de arriba. Se reusa la que ya esté puesta en vez de
         insertar otra, que si no cada repintado dejaría una copia más. */
      var linea = pies[i].parentNode
        ? pies[i].parentNode.querySelector('.vault-carrito__envio')
        : null;
      if (!linea) {
        linea = elemento('p', 'vault-carrito__envio');
        if (pies[i].parentNode) pies[i].parentNode.insertBefore(linea, pies[i]);
      }
      linea.textContent = textoDeEnvio(plan);
      pintarAvisoDePunto(pies[i], plan);
    }
  }

  /* ── El aviso del punto preseleccionado ─────────────────────────────────
     El checkout ordena las tarifas de $0 por nombre y marca de entrada la
     primera: hoy, "Recoge en Bazaar of the Lions". No hay forma de cambiar eso
     sin Shopify Plus — el orden en que creamos las tarifas no cuenta, se probó
     el 15-oct contra el cotizador de la tienda. Cuando entró Bazaar (27-sep) el
     default dejó de ser Hobby Mart, y los compradores de siempre que pagan sin
     mirar empezaron a caer ahí (#VA-2038, #VA-2049, #VA-2051, #VA-2077,
     #VA-2103, #VA-2140).

     Lo único que el carrito puede hacer es decirlo justo antes del botón, en
     caja y no como una línea más del pie: es la última pantalla antes de la
     elección, y una línea gris más es exactamente lo que ya no se lee.
     Sin punto en la región no hay nada que revisar, así que no sale. */
  function pintarAvisoDePunto(pie, plan) {
    var padre = pie.parentNode;
    if (!padre) return;
    var aviso = padre.querySelector('.vault-carrito__aviso-punto');
    if (!plan.recogerDisponible) {
      if (aviso) padre.removeChild(aviso);
      return;
    }
    if (aviso) return;
    aviso = elemento('p', 'vault-carrito__aviso-punto');
    aviso.appendChild(icono('pin'));
    var texto = elemento('span', 'vault-carrito__aviso-texto');
    texto.appendChild(elemento('strong', '', 'Al pagar, revisa tu punto de entrega. '));
    texto.appendChild(
      document.createTextNode('La lista marca de entrada el primero, no el que usas siempre.')
    );
    aviso.appendChild(texto);
    padre.insertBefore(aviso, pie.nextSibling);
  }

  /* ───────────────────────── Los grupos en la lista ─────────────────────────
     Un grupo por LLEGADA, no por modo de entrega. Dos idas al mismo punto en
     fechas distintas son dos grupos, porque son dos viajes del comprador:
     fundirlas sería la mentira más cara de todas —la persona iría una vez y
     volvería con la mitad. */
  function limpiarLista(raiz) {
    var viejos = raiz.querySelectorAll('[data-vault-grupo], .vault-carrito__origen');
    for (var i = 0; i < viejos.length; i++) viejos[i].parentNode.removeChild(viejos[i]);
  }

  function columnasDe(raiz) {
    var encabezados = raiz.querySelectorAll('thead th');
    if (!encabezados.length) return 5;
    var total = 0;
    for (var i = 0; i < encabezados.length; i++) {
      total += parseInt(encabezados[i].getAttribute('colspan'), 10) || 1;
    }
    return total || 5;
  }

  /**
   * El stack de miniaturas: 4 cartas encimadas y un `+N` con el resto.
   *
   * Es lo que hace que un grupo plegado siga siendo revisable — se confirma de
   * un vistazo que ahí están las cartas que uno cree, sin abrirlo. Reusa el
   * `src` que Dawn ya pintó en cada fila, así que no cuesta ni una petición.
   * Sin imágenes no se dibuja nada: un stack de rectángulos vacíos no confirma
   * nada y ocupa lo mismo.
   */
  var TOPE_STACK = 4;

  function stackDeGrupo(grupo) {
    var fuentes = [];
    for (var i = 0; i < grupo.lineas.length && fuentes.length < TOPE_STACK; i++) {
      if (grupo.lineas[i].imagen) fuentes.push(grupo.lineas[i].imagen);
    }
    if (!fuentes.length) return null;

    var caja = elemento('span', 'vault-grupo__stack');
    caja.setAttribute('aria-hidden', 'true');
    for (var j = 0; j < fuentes.length; j++) {
      var img = document.createElement('img');
      img.className = 'vault-grupo__mini';
      img.setAttribute('src', fuentes[j]);
      img.setAttribute('alt', '');
      img.setAttribute('loading', 'lazy');
      caja.appendChild(img);
    }
    var resto = (grupo.cartas || 0) - fuentes.length;
    if (resto > 0) caja.appendChild(elemento('span', 'vault-grupo__mas', '+' + resto));
    return caja;
  }

  /** "11 cartas · $9,735 · Sí, es otra ida al mismo punto." */
  function cuentaDeGrupo(grupo) {
    var partes = [cartas(grupo.cartas || 0)];
    if (grupo.subtotal !== null && grupo.subtotal !== undefined) {
      partes.push(pesos(Math.round(grupo.subtotal / 100)));
    }
    if (grupo.aviso) partes.push(grupo.aviso);
    return partes.join(' · ');
  }

  /**
   * La cabecera de un grupo, que en F8 es también su interruptor.
   *
   * Sigue siendo un `<tr>` con un `<td colspan>` —Dawn convierte la tabla en
   * bloques abajo de 750px y una fila que no lo sea se colapsa a nada— pero
   * ahora lleva dentro un `<button aria-expanded>` que ocupa toda la celda.
   */
  function filaDeGrupo(grupo, columnas, abierto) {
    var tr = elemento('tr', 'vault-grupo');
    tr.setAttribute('data-vault-grupo', grupo.id);
    var td = elemento('td', 'vault-grupo__celda');
    td.setAttribute('colspan', String(columnas));

    var boton = elemento('button', 'vault-grupo__cabeza');
    boton.type = 'button';
    boton.setAttribute('aria-expanded', abierto ? 'true' : 'false');
    /* Las filas que este botón abre y cierra, para quien navega con lector de
       pantalla y no ve el chevron girar. */
    var ids = [];
    for (var i = 0; i < grupo.lineas.length; i++) {
      if (grupo.lineas[i].fila.id) ids.push(grupo.lineas[i].fila.id);
    }
    if (ids.length) boton.setAttribute('aria-controls', ids.join(' '));

    /* La cabecera plegada dice la fecha en CORTO —"2–5 sep", "~11 sep"— porque
       tiene un renglón y la larga se lo comía en tres. La versión completa, con
       el punto y todo, va en la etiqueta accesible: quien la escucha con lector
       de pantalla no pierde una sola palabra de la promesa. */
    boton.setAttribute(
      'aria-label',
      [
        grupo.titulo,
        grupo.donde ? (grupo.icono === 'caja' ? 'a ' : 'en ') + grupo.donde : '',
        grupo.detalle,
        cuentaDeGrupo(grupo),
      ]
        .filter(Boolean)
        .join(' · ')
    );

    boton.appendChild(icono('chevron'));

    var texto = elemento('span', 'vault-grupo__texto');
    var titulo = elemento('span', 'vault-grupo__titulo');
    titulo.appendChild(icono(grupo.icono));
    titulo.appendChild(
      document.createTextNode(
        grupo.titulo + (grupo.detalleCorto ? ' · ' + grupo.detalleCorto : '')
      )
    );
    texto.appendChild(titulo);
    texto.appendChild(elemento('span', 'vault-grupo__detalle', cuentaDeGrupo(grupo)));
    boton.appendChild(texto);

    var stack = stackDeGrupo(grupo);
    if (stack) boton.appendChild(stack);

    boton.addEventListener('click', function () {
      var ahora = !plegados[grupo.id];
      plegados[grupo.id] = ahora;
      medir('carrito_grupo', (ahora ? 'plegar' : 'expandir') + ':' + grupo.id);
      aplicarPlegado(tr, grupo, !ahora);
    });

    td.appendChild(boton);
    tr.appendChild(td);
    return tr;
  }

  /**
   * Plegar es ESCONDER, nunca desmontar.
   *
   * Las filas se quedan donde están, con su `data-index`, su input de cantidad y
   * su botón de quitar intactos: el JS de carrito de Dawn sigue encontrándolas y
   * un re-render suyo sigue funcionando. Sacarlas del DOM —o moverlas a otro
   * padre— rompería el `updateQuantity` de `cart.js`, que es exactamente lo que
   * este acordeón no puede permitirse.
   */
  function aplicarPlegado(tr, grupo, abierto) {
    tr.classList.toggle('vault-grupo--abierto', Boolean(abierto));
    var boton = tr.querySelector('.vault-grupo__cabeza');
    if (boton) boton.setAttribute('aria-expanded', abierto ? 'true' : 'false');
    for (var i = 0; i < grupo.lineas.length; i++) {
      grupo.lineas[i].fila.hidden = !abierto;
      grupo.lineas[i].fila.classList.toggle('vault-linea--en-grupo', Boolean(abierto));
      /* La última es la que cierra la caja por abajo: borde y esquinas. Se marca
         desde aquí porque `:last-child` no sirve —las filas de un grupo son
         hermanas de las del siguiente dentro del mismo `<tbody>`— y el CSS no
         tiene forma de saber dónde termina un grupo sin que se lo digan. */
      grupo.lineas[i].fila.classList.toggle(
        'vault-linea--ultima',
        Boolean(abierto) && i === grupo.lineas.length - 1
      );
    }
  }

  /** El lugar baja al renglón de la carta, y sólo donde explica algo. */
  function marcarOrigen(grupo) {
    var marcar = grupo.marcar || [];
    for (var i = 0; i < marcar.length; i++) {
      if (!marcar[i].single || !marcar[i].regiones.length) continue;
      var destino = marcar[i].fila.querySelector('.cart-item__details');
      if (!destino) continue;
      destino.appendChild(
        elemento('p', 'vault-carrito__origen', 'desde ' + marcar[i].regiones.join(' o '))
      );
    }
  }

  function pintarGrupos(raiz, plan) {
    limpiarLista(raiz);
    if (!plan.agrupa) {
      /* Sin agrupar no hay acordeón que aplicar, y hay que deshacer el de un
         render anterior: si el carrito se queda sin dato de región a media
         sesión, las filas escondidas tienen que volver a verse. */
      mostrarTodas(raiz);
      return;
    }

    var cuerpo = raiz.querySelector('tbody');
    if (!cuerpo) return;

    var grupos = plan.grupos;
    var columnas = columnasDe(raiz);

    for (var g = 0; g < grupos.length; g++) {
      /* Ausente = abierto: los grupos nacen abiertos y sólo se quedan plegados
         los que el comprador plegó en esta página. Como `plegados` vive en el
         módulo y la clave es el id del grupo, el estado sobrevive a que Dawn
         reemplace el HTML de `.js-contents` al cambiar una cantidad. */
      var abierto = !plegados[grupos[g].id];
      var tr = filaDeGrupo(grupos[g], columnas, abierto);
      cuerpo.appendChild(tr);
      /* Reordenar los `<tr>` es seguro: Dawn identifica cada línea por su
         `data-index`, que no cambia al moverla de sitio. */
      for (var i = 0; i < grupos[g].lineas.length; i++) {
        cuerpo.appendChild(grupos[g].lineas[i].fila);
      }
      marcarOrigen(grupos[g]);
      aplicarPlegado(tr, grupos[g], abierto);
    }
  }

  function mostrarTodas(raiz) {
    var filas = raiz.querySelectorAll('[data-vault-linea]');
    for (var i = 0; i < filas.length; i++) {
      filas[i].hidden = false;
      filas[i].classList.remove('vault-linea--en-grupo', 'vault-linea--ultima');
    }
  }

  /* ────────────────── La barra de pago (F9) ─────────────────────────────────
     En un teléfono el total y el botón de pagar quedan hasta abajo: con doce
     cartas hay que scrollear dos pantallas para pagar algo que ya se decidió.
     Esta barra sube la ÚNICA acción verdadera del carrito —pagar— y el dato que
     la justifica —el total— a donde el pulgar ya está.

     No contradice el pivote de un-solo-dueño: no decide nada. No lleva modo, ni
     punto, ni entregas. Lo que decide la entrega sigue siendo el checkout.

     El botón es un `<button name="checkout" form="cart">` DE VERDAD, no un
     disparador sintético: gracias a `form=` puede vivir fuera del formulario y
     enviarlo igual, con el mismo `name` que Dawn, así que pasa por el mismo
     camino que el botón del pie. Los botones dinámicos (PayPal, GPay) NO se
     tocan ni se duplican —Shopify los inyecta y no sobreviven a moverlos— y se
     quedan en el pie para quien baje. */
  var ALTO_BARRA = 72;

  function esMovil() {
    /* `innerWidth` y no `matchMedia`: es el mismo umbral de 750px del tema, se
       lee igual en cualquier navegador y se puede fijar en el harness. */
    return (window.innerWidth || 0) < 750;
  }

  /** El total tal cual lo escribió Shopify, con su formato de moneda, o ''. */
  function totalDelDom() {
    var celda = document.querySelector('.totals__total-value');
    return celda ? celda.textContent.trim() : '';
  }

  function totalDeLaPagina(plan) {
    /* Sin el pie a la vista —un tema recortado, un re-render a medias— se cae al
       formato del módulo antes que enseñar un hueco. */
    return totalDelDom() || (plan && plan.total ? pesos(Math.round(plan.total / 100)) : '');
  }

  function cartasDelCarrito(raiz) {
    var filas = raiz.querySelectorAll('[data-vault-linea]');
    var n = 0;
    for (var i = 0; i < filas.length; i++) {
      n += parseInt(filas[i].getAttribute('data-cantidad'), 10) || 1;
    }
    return n;
  }

  function botonRealDePago() {
    return document.querySelector('.cart__ctas [name="checkout"]');
  }

  function quitarBarraPago() {
    if (!barraPago) return;
    if (barraPago.parentNode) barraPago.parentNode.removeChild(barraPago);
    barraPago = null;
    document.body.classList.remove('vault-con-barra-pago');
    document.body.style.removeProperty('--vault-barra-pago-alto');
  }

  function asegurarBarraPago() {
    if (barraPago) return barraPago;
    barraPago = elemento('div', 'vault-barra-pago');
    barraPago.setAttribute('data-vault-barra-pago', '1');
    barraPago.hidden = true;

    var cuenta = elemento('div', 'vault-barra-pago__cuenta');
    cuenta.appendChild(elemento('span', 'vault-barra-pago__total'));
    cuenta.appendChild(elemento('span', 'vault-barra-pago__cartas'));
    barraPago.appendChild(cuenta);

    var boton = elemento('button', 'vault-barra-pago__boton button');
    boton.type = 'submit';
    boton.name = 'checkout';
    boton.setAttribute('form', 'cart');
    boton.setAttribute('data-vault-pagar', '1');
    boton.textContent = 'Pagar pedido';
    barraPago.appendChild(boton);


    document.body.appendChild(barraPago);
    return barraPago;
  }

  function pintarBarraPago(plan, panel) {
    /* Sólo la PÁGINA del carrito y sólo en teléfono. El drawer nunca: allá el
       botón de pagar ya está a la vista, y una barra fija encima del panel
       taparía justo lo que se acaba de abrir. */
    if (!panel || !esMovil() || !botonRealDePago()) {
      quitarBarraPago();
      return;
    }

    var raiz = (panel.closest && panel.closest('[data-vault-carrito-raiz]')) || document;
    var caja = asegurarBarraPago();

    totalPintado = totalDeLaPagina(plan);
    caja.querySelector('.vault-barra-pago__total').textContent = totalPintado;
    var n = cartasDelCarrito(raiz);
    /* Desde esta barra se paga sin bajar hasta el aviso del punto, así que lo
       repite en corto (ver `pintarAvisoDePunto`). */
    /* El conteo va tenue; el recordatorio del punto, en amarillo para que se
       lea (antes todo el renglón iba al 65 % y el recordatorio se perdía). */
    var linea = caja.querySelector('.vault-barra-pago__cartas');
    linea.textContent = '';
    if (n) linea.appendChild(elemento('span', 'vault-barra-pago__n', cartas(n)));
    if (plan && plan.recogerDisponible) {
      if (n) linea.appendChild(document.createTextNode(' · '));
      linea.appendChild(elemento('span', 'vault-barra-pago__punto', 'revisa tu punto al pagar'));
    }

    evaluarBarraPago();
  }

  /**
   * Aparece cuando el botón de pagar de verdad todavía viene por debajo del
   * pliegue, y se esconde en cuanto asoma: dos botones de pagar a la vista al
   * mismo tiempo es una pregunta que nadie hizo.
   */
  function evaluarBarraPago() {
    if (!barraPago) return;
    var real = botonRealDePago();
    if (!real) return quitarBarraPago();
    var alto = window.innerHeight || 0;
    var visible = false;
    try {
      visible = real.getBoundingClientRect().top > alto - 8;
    } catch (e) {
      visible = false;
    }
    barraPago.hidden = !visible;
    /* La clase la lee el CSS para subir la burbuja de WhatsApp por encima de la
       barra: si no, se pisan justo en la esquina donde vive el pulgar.

       Y el ALTO se publica medido, no calculado. La primera versión sumaba a
       mano el min-height, el padding y el safe-area, y le salió 72px donde el
       navegador dibujaba 93: la burbuja quedaba 5px encima de la barra. Con el
       alto real no hay aritmética que se pueda desfasar cuando cambie un
       padding, ni safe-area que adivinar — ya viene dentro. */
    document.body.classList.toggle('vault-con-barra-pago', visible);
    if (visible && barraPago.offsetHeight) {
      document.body.style.setProperty('--vault-barra-pago-alto', barraPago.offsetHeight + 'px');
    }
  }

  function engancharBarraPago() {
    var alScroll = function () {
      if (barraAgendada) return;
      barraAgendada = true;
      var correr = function () {
        barraAgendada = false;
        evaluarBarraPago();
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(correr);
      else setTimeout(correr, 16);
    };
    window.addEventListener('scroll', alScroll, { passive: true });
    /* En `resize` no basta con recolocar: cruzar los 750px hacia escritorio
       tiene que DESMONTAR la barra, no sólo esconderla. */
    window.addEventListener('resize', function () {
      if (!esMovil()) quitarBarraPago();
      else renderTodo();
      alScroll();
    });
  }

  /* ───────────────────────── Render ───────────────────────── */

  function planDelPanel(panel, raiz) {
    return planDeEntrega({
      lineas: lineasDe(raiz),
      regionComprador: leerLocal(LLAVE_REGION),
      regiones: datos.regiones,
      valija: datos.valija,
      total: parseInt(panel.getAttribute('data-total'), 10) || 0,
      hoy: hoyMx(),
    });
  }

  function renderPanel(panel) {
    var raiz = (panel.closest && panel.closest('[data-vault-carrito-raiz]')) || document;
    var plan = planDelPanel(panel, raiz);

    panel.textContent = '';

    if (panel.getAttribute('data-modo') === 'mini') {
      pintarMini(panel, plan);
      return plan;
    }

    if (!plan.recogerDisponible && plan.regionBase) {
      panel.appendChild(
        elemento(
          'p',
          'vault-carrito__nota',
          'En ' +
            plan.regionBase +
            ' todavía no hay punto de entrega, así que tu pedido va por envío.'
        )
      );
    }

    /* El encabezado del recibo. Lo que sigue abajo ya no es "tu carrito": son
       las entregas, y conviene decirlo antes de que empiecen. */
    /* Con una sola entrega la cabecera de la caja ya lo dice (fecha y cartas);
       repetirlo arriba es ruido. Solo se anuncia cuando son varias. */
    if (plan.agrupa && plan.entregas > 1) {
      panel.appendChild(
        elemento(
          'p',
          'vault-carrito__entregas',
          plan.entregas > 1
            ? 'Tu pedido llega en ' + plan.entregas + ' entregas'
            : 'Tu pedido llega en 1 entrega'
        )
      );
    }

    /* Sin nada que decir el hueco se queda escondido, que es como nació: un
       carrito degradado no enseña una caja vacía. */
    panel.hidden = !panel.childNodes.length;
    pintarGrupos(raiz, plan);
    return plan;
  }

  /** La versión mínima del drawer: una línea, y el detalle vive en el carrito. */
  function pintarMini(panel, plan) {
    if (!plan.agrupa) {
      panel.hidden = true;
      return;
    }
    var linea = elemento('p', 'vault-carrito__mini');
    linea.appendChild(icono('tienda'));
    linea.appendChild(
      document.createTextNode(
        plan.entregas > 1
          ? 'Tu pedido llega en ' + plan.entregas + ' entregas'
          : 'Tu pedido llega en 1 entrega'
      )
    );
    panel.appendChild(linea);
    panel.hidden = false;
  }

  function renderTodo() {
    if (!datos.listo) return;
    var paneles = document.querySelectorAll('[data-vault-carrito]');
    var ultimo = null;
    var panelCompleto = null;
    var planCompleto = null;
    pintados = [];
    for (var i = 0; i < paneles.length; i++) {
      ultimo = renderPanel(paneles[i]);
      pintados.push(paneles[i]);
      /* La barra sigue al comparador de verdad, no a la línea del drawer. */
      if (paneles[i].getAttribute('data-modo') !== 'mini') {
        panelCompleto = paneles[i];
        planCompleto = ultimo;
      }
    }
    if (ultimo) {
      pintarPie(ultimo);
      entregasAlPagar = ultimo.agrupa ? ultimo.entregas : 0;
    }
    pintarBarraPago(planCompleto, panelCompleto);
  }

  /* ───────────────────────── Instrumentación del checkout ─────────────────
     Con cuántas entregas se fue a pagar, y con qué opción comparada. Delegado
     en `document` porque el botón de pagar lo reemplaza Dawn en cada cambio de
     cantidad, y un listener sobre el botón se perdería con él. */
  function engancharCheckout() {
    document.addEventListener('click', function (ev) {
      var boton = ev.target && ev.target.closest ? ev.target.closest('[name="checkout"]') : null;
      if (!boton) return;
      /* Con cuántas entregas se fue a pagar. Ya no lleva el modo: no hay modo
         que comparar aquí, lo elige el checkout con sus tarifas. */
      medir('carrito_checkout', entregasAlPagar);
      /* Y DE DÓNDE se pagó, que es lo único que puede decir si la barra sirvió:
         se compara contra los que bajaron hasta el pie. */
      medir('carrito_pagar_desde', boton.getAttribute('data-vault-pagar') ? 'barra' : 'pie');
    });
  }

  /* ───────────────────────── Arranque ─────────────────────────
     Dawn reemplaza el HTML de `.js-contents` en cada cambio de cantidad, y con
     él se van este panel y las cabeceras de grupo. El observer sólo reacciona
     cuando aparece un panel que NO es el que se pintó —o un pie sin marcar—,
     así no se despierta con sus propias mutaciones ni con las de terceros. */
  function hayQueRepintar() {
    var paneles = document.querySelectorAll('[data-vault-carrito]');
    if (!paneles.length) return false;
    if (paneles.length !== pintados.length) return true;
    for (var i = 0; i < paneles.length; i++) {
      if (paneles[i] !== pintados[i]) return true;
    }
    if (document.querySelectorAll('.cart__delivery-estimate:not([data-vault-pie])').length > 0) {
      return true;
    }
    /* Y si el total de la página ya no es el que la barra está enseñando: Dawn
       repinta el pie y el listado por separado, y un total viejo colgado del
       pulgar es peor que no tener barra.

       Se compara contra el DOM y no contra `totalDeLaPagina`, que tiene
       respaldo: sin celda de total el respaldo nunca coincidiría con lo pintado
       y esto se volvería un repintado en bucle con cada mutación de la página. */
    var enPagina = totalDelDom();
    return Boolean(barraPago) && Boolean(enPagina) && enPagina !== totalPintado;
  }

  function arrancar() {
    if (!document.querySelector('[data-vault-carrito]')) return;

    engancharCheckout();
    engancharBarraPago();

    pedirEstado(function (payload) {
      datos.regiones = payload ? payload.regiones : null;
      datos.valija = payload ? payload.valija || null : null;
      datos.listo = true;
      renderTodo();
    });

    if (!window.MutationObserver) return;
    var agendado = false;
    new MutationObserver(function () {
      if (agendado || !hayQueRepintar()) return;
      agendado = true;
      setTimeout(function () {
        agendado = false;
        renderTodo();
      }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* El núcleo probable, para el harness de node:vm — este repo no tiene build ni
     runner, y auditar los módulos del tema así es lo que ya hace el equipo. */
  window.vaultCarrito = {
    planDeEntrega: planDeEntrega,
    precioDeEnvio: precioDeEnvio,
    fechaDeValija: fechaDeValija,
    fechaLarga: fechaLarga,
    fechaRango: fechaRango,
    fechaCortaDe: fechaCortaDe,
    resumirGrupo: resumirGrupo,
  };

  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
